/**
 * Mobile Rankings Modal Component
 * Shows all teams ranked for a selected stat category
 * Mobile-optimized version of desktop RankingsModal
 */

import React, { useState, useEffect } from 'react';
import { X, TrendingUp } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';

const C = dynastyTheme.tokens.colors;

/**
 * MobileRankingsModal Component
 * Shows all teams ranked for a selected stat category
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Close handler
 * @param {Object} allRankings - Full rankings object from API (all_rankings)
 * @param {string} yourTeamId - Current user's team ID for highlighting
 * @param {string} defaultCategory - Initial stat category to display
 * @param {string} statsDate - Date for the rankings
 */
const MobileRankingsModal = ({
  isOpen,
  onClose,
  allRankings,
  yourTeamId,
  defaultCategory = 'total_hr',
  statsDate = '',
  // Chunk 3.14 — tile-enrichment props (2×2 grid + big sparkline above
  // the rankings list). All optional — modal renders cleanly without them
  // (legacy callers see the same UX as before).
  weekly = null,
  lastWeek = null,
  season = null,
  weeklyHistory = null,
  wow = null,
  weekMeta = null,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);

  // Update selectedCategory when defaultCategory changes (when user clicks different tile)
  useEffect(() => {
    if (defaultCategory) {
      setSelectedCategory(defaultCategory);
    }
  }, [defaultCategory]);

  if (!isOpen || !allRankings) return null;

  // Map category keys to display labels
  const categoryLabels = {
    'total_runs': 'Runs',
    'total_hr': 'Home Runs',
    'total_rbi': 'RBI',
    'total_sb': 'Stolen Bases',
    'avg': 'Batting Average',
    'ops': 'OPS',
    'total_wins': 'Wins',
    'total_saves': 'Saves',
    'era': 'ERA',
    'whip': 'WHIP',
    'total_k': 'Strikeouts',
    'total_qs': 'Quality Starts'
  };

  // Format value based on stat type
  const formatValue = (category, value) => {
    if (category === 'avg' || category === 'ops') {
      return formatBattingAvg(value);
    }
    if (category === 'era') {
      return formatERA(value);
    }
    if (category === 'whip') {
      return formatBattingAvg(value);
    }
    return value;
  };

  // Get rankings for selected category
  const rankings = allRankings[selectedCategory] || [];

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/70"
      style={{ zIndex: 999999 }}
      onClick={onClose}
    >
      <div className="relative flex items-center justify-center min-h-screen" style={{ padding: '12px' }}>
        <div
          className={`${dynastyTheme.components.card.base} flex flex-col`}
          style={{
            width: '100%',
            maxWidth: '288px',
            maxHeight: '56.25vh',
            border: `1px solid ${dynastyTheme.tokens.colors.neutral[600]}`,
            boxShadow: `0 25px 50px -12px ${withAlpha(C.black, 0.25)}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b" style={{ 
            padding: '9px', 
            borderColor: dynastyTheme.tokens.colors.neutral[700] 
          }}>
            <div className="flex items-center" style={{ gap: '6px' }}>
              <TrendingUp className={dynastyTheme.classes.text.primary} style={{ width: '15px', height: '15px' }} />
              <h2 className={`font-bold ${dynastyTheme.classes.text.white}`} style={{ fontSize: '12px' }}>
                Rankings
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.neutral} transition-colors rounded-lg`}
              style={{ padding: '3px' }}
            >
              <X style={{ width: '15px', height: '15px' }} />
            </button>
          </div>

          {/* Category Selector */}
          <div className="border-b bg-neutral-800/50" style={{ 
            padding: '9px', 
            borderColor: dynastyTheme.tokens.colors.neutral[700] 
          }}>
            <label className={`block font-medium ${dynastyTheme.classes.text.neutralLight}`} style={{ 
              fontSize: '9px', 
              marginBottom: '4.5px' 
            }}>
              Stat Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`w-full ${dynastyTheme.components.input}`}
              style={{ 
                padding: '6px 9px', 
                fontSize: '9px' 
              }}
            >
              <optgroup label="⚾ Hitting Stats">
                <option value="total_runs">Runs</option>
                <option value="total_hr">Home Runs</option>
                <option value="total_rbi">RBI</option>
                <option value="total_sb">Stolen Bases</option>
                <option value="avg">Batting Average</option>
                <option value="ops">OPS</option>
              </optgroup>
              <optgroup label="🥎 Pitching Stats">
                <option value="total_wins">Wins</option>
                <option value="total_saves">Saves</option>
                <option value="era">ERA</option>
                <option value="whip">WHIP</option>
                <option value="total_k">Strikeouts</option>
                <option value="total_qs">Quality Starts</option>
              </optgroup>
            </select>

            {statsDate && (
              <div className={`${dynastyTheme.classes.text.neutralLight} text-center`} style={{
                fontSize: '8px',
                marginTop: '4.5px'
              }}>
                {formatDate(statsDate)}
              </div>
            )}
          </div>

          {/* ════════════════════════════════════════════════════════════
              Chunk 3.14 — Tile-enrichment section: 2×2 grid + big sparkline.
              Rendered ABOVE the rankings list when enrichment props are
              provided. Shows the user's team's Today / This Week (with
              trend) / Last Week / Season values for the selected category,
              plus the 6-week sparkline with week labels.
              ════════════════════════════════════════════════════════════ */}
          {(weekly || season || weeklyHistory) && (() => {
            const isRate = ['avg', 'ops', 'era', 'whip'].includes(selectedCategory);
            const fmt = (v) => {
              if (v === null || v === undefined) return '—';
              return formatValue(selectedCategory, v);
            };
            const fmtDelta = (d) => {
              if (d === null || d === undefined) return '—';
              if (isRate) {
                if (selectedCategory === 'era') return `${d >= 0 ? '+' : ''}${Number(d).toFixed(2)}`;
                return `${d >= 0 ? '+' : ''}${Number(d).toFixed(3).replace(/^0\./, '.').replace(/^-0\./, '-.')}`;
              }
              return `${d >= 0 ? '+' : ''}${d}`;
            };

            // Today's value for the user's team — read from the existing
            // all_rankings list (we already have it; no extra prop needed).
            const yourTeamRow = rankings.find(r => r.team_id === yourTeamId);
            const todayValue = yourTeamRow ? yourTeamRow.value : null;
            const todayRank = yourTeamRow ? yourTeamRow.rank : null;

            const weeklyValue = weekly?.[selectedCategory];
            const lastWeekValue = lastWeek?.[selectedCategory];
            const seasonValue = season?.[selectedCategory];
            const w = wow?.[selectedCategory] || { delta: null, direction: 'none' };
            const dirColor =
              w.direction === 'up'   ? C.spark.up :
              w.direction === 'down' ? C.spark.down :
              w.direction === 'flat' ? C.spark.flat :
              C.spark.none;
            const arrowGlyph =
              w.direction === 'up'   ? '▲' :
              w.direction === 'down' ? '▼' :
              w.direction === 'flat' ? '–' :
              '○';

            const history = (weeklyHistory?.[selectedCategory] || []).slice(0, 6);
            const numeric = history.filter(v => v !== null && v !== undefined).map(Number);
            const minV = numeric.length ? Math.min(...numeric) : 0;
            const maxV = numeric.length ? Math.max(...numeric) : 1;
            const range = (maxV - minV) || 1;
            // viewBox: 0..264 horizontal × 0..72 vertical; latest at x=240
            const xPositions = [24, 72, 120, 168, 216, 240];
            const ys = history.map(v => (v === null || v === undefined) ? null : 60 - ((v - minV) / range) * 50);

            const segments = [];
            let current = [];
            for (let i = 0; i < Math.min(6, ys.length); i++) {
              if (ys[i] === null) {
                if (current.length >= 2) segments.push(current);
                current = [];
              } else {
                current.push(`${xPositions[i]},${ys[i]}`);
              }
            }
            if (current.length >= 2) segments.push(current);

            const lastIdx = ys.map((y, i) => y !== null ? i : -1).filter(i => i >= 0).pop();

            // Week labels: prefer weekMeta.sparkline_week_starts if provided
            // (parse "YYYY-MM-DD" → "Wnn" ISO week). Fallback: "W1..W6".
            const weekLabels = (weekMeta?.sparkline_week_starts || []).slice(0, 6).map((ws, i) => {
              try {
                const d = new Date(ws + 'T00:00:00');
                // Compute ISO week number
                const target = new Date(d.valueOf());
                const dayNr = (target.getDay() + 6) % 7;
                target.setDate(target.getDate() - dayNr + 3);
                const firstThursday = new Date(target.getFullYear(), 0, 4);
                const diff = target - firstThursday;
                const wk = 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
                return `W${wk}`;
              } catch (_) {
                return `W${i + 1}`;
              }
            });
            while (weekLabels.length < 6) weekLabels.push(`W${weekLabels.length + 1}`);

            return (
              <div className="border-b" style={{ padding: '10px 9px', borderColor: dynastyTheme.tokens.colors.neutral[700] }}>
                {/* 2×2 grid — Today / This Week / Last Week / Season */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginBottom: '10px' }}>
                  <div style={{ padding: '6px 7px', borderRadius: '6px', background: withAlpha(C.goldBase, 0.07), boxShadow: `inset 0 0 0 1px ${withAlpha(C.goldBase, 0.22)}` }}>
                    <div style={{ fontSize: '8px', fontWeight: 700, color: C.spark.flat, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Today{todayRank ? ` · ${todayRank}/${rankings.length}` : ''}
                    </div>
                    <div style={{ fontSize: '17px', fontWeight: 800, color: C.white, marginTop: '3px', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                      {fmt(todayValue)}
                    </div>
                  </div>
                  <div style={{ padding: '6px 7px', borderRadius: '6px', background: `${dirColor}10`, boxShadow: `inset 0 0 0 1px ${dirColor}33` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '8px', fontWeight: 700, color: C.spark.flat, textTransform: 'uppercase', letterSpacing: '0.05em' }}>This Wk</span>
                      <span style={{ fontSize: '9px', fontWeight: 700, color: dirColor, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ fontSize: '7px' }}>{arrowGlyph}</span>
                        {fmtDelta(w.delta)}
                      </span>
                    </div>
                    <div style={{ fontSize: '17px', fontWeight: 800, color: C.white, marginTop: '3px', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                      {fmt(weeklyValue)}
                    </div>
                  </div>
                  <div style={{ padding: '6px 7px', borderRadius: '6px', background: withAlpha(C.white, 0.025), boxShadow: `inset 0 0 0 1px ${withAlpha(C.spark.none, 0.45)}` }}>
                    <div style={{ fontSize: '8px', fontWeight: 700, color: C.spark.flat, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Wk</div>
                    <div style={{ fontSize: '17px', fontWeight: 800, color: C.spark.flat, marginTop: '3px', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                      {fmt(lastWeekValue)}
                    </div>
                  </div>
                  <div style={{ padding: '6px 7px', borderRadius: '6px', background: withAlpha(C.white, 0.025), boxShadow: `inset 0 0 0 1px ${withAlpha(C.spark.none, 0.45)}` }}>
                    <div style={{ fontSize: '8px', fontWeight: 700, color: C.spark.flat, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Season</div>
                    <div style={{ fontSize: '17px', fontWeight: 800, color: C.spark.flat, marginTop: '3px', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                      {fmt(seasonValue)}
                    </div>
                  </div>
                </div>

                {/* 6-week sparkline */}
                <div style={{ padding: '8px', borderRadius: '6px', background: withAlpha(C.white, 0.02), boxShadow: `inset 0 0 0 1px ${withAlpha(C.spark.none, 0.35)}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '8px', fontWeight: 700, color: C.spark.flat, textTransform: 'uppercase', letterSpacing: '0.18em' }}>6-Week Trend</span>
                    <span style={{ fontSize: '9px', fontWeight: 700, color: dirColor, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ fontSize: '7px' }}>{arrowGlyph}</span>
                      vs last week {fmtDelta(w.delta)}
                    </span>
                  </div>
                  <svg viewBox="0 0 264 72" style={{ width: '100%', height: '64px' }}>
                    {/* Horizontal grid lines */}
                    <line x1="0" y1="12" x2="264" y2="12" stroke={C.surface[860]} strokeWidth="0.5" strokeDasharray="2,3" />
                    <line x1="0" y1="36" x2="264" y2="36" stroke={C.surface[860]} strokeWidth="0.5" strokeDasharray="2,3" />
                    <line x1="0" y1="60" x2="264" y2="60" stroke={C.surface[860]} strokeWidth="0.5" strokeDasharray="2,3" />

                    {/* Segmented polylines for null-gapped data */}
                    {segments.map((seg, i) => (
                      <polyline
                        key={i}
                        points={seg.join(' ')}
                        fill="none"
                        stroke={dirColor}
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                    ))}

                    {/* Dots at each present point */}
                    {ys.map((y, i) => (y !== null ? (
                      <circle
                        key={i}
                        cx={xPositions[i]}
                        cy={y}
                        r={i === lastIdx ? 3.5 : 2.5}
                        fill={i === lastIdx ? dirColor : C.surface[720]}
                        stroke={dirColor}
                        strokeWidth="1.5"
                      />
                    ) : null))}

                    {/* Highlight ring on latest */}
                    {lastIdx !== undefined && lastIdx !== null && ys[lastIdx] !== null && (
                      <circle cx={xPositions[lastIdx]} cy={ys[lastIdx]} r="6" fill={dirColor} opacity="0.2" />
                    )}

                    {/* Inline value labels under each point */}
                    {history.map((v, i) => (
                      <text
                        key={i}
                        x={xPositions[i]}
                        y="70"
                        fontSize="7.5"
                        fontWeight={i === lastIdx ? '700' : '500'}
                        fill={i === lastIdx ? dirColor : C.coolGray[620]}
                        textAnchor="middle"
                        fontVariantNumeric="tabular-nums"
                      >
                        {v !== null && v !== undefined ? (isRate ? formatValue(selectedCategory, v) : v) : '—'}
                      </text>
                    ))}
                  </svg>
                  {/* Week labels */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', marginTop: '2px' }}>
                    {weekLabels.map((lbl, i) => (
                      <div
                        key={i}
                        style={{
                          textAlign: 'center',
                          fontSize: '8px',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          color: i === lastIdx ? C.primary : C.spark.none,
                          textTransform: 'uppercase',
                        }}
                      >
                        {lbl}
                      </div>
                    ))}
                  </div>
                  {/* Caption */}
                  {weekMeta?.current_week_end_today && weekMeta?.current_week_start && (
                    <div style={{ marginTop: '5px', paddingTop: '5px', borderTop: `1px solid ${withAlpha(C.spark.none, 0.25)}`, fontSize: '8.5px', color: C.coolGray[620], lineHeight: 1.4 }}>
                      Current week is in-progress (Mon → today). Counting stats build through the week.
                    </div>
                  )}
                </div>

                {/* Section divider above the team list */}
                <div style={{ fontSize: '8px', fontWeight: 700, color: C.coolGray[620], textTransform: 'uppercase', letterSpacing: '0.22em', marginTop: '10px', marginBottom: '-2px' }}>
                  League Ranking · Today
                </div>
              </div>
            );
          })()}

          {/* Rankings List - Scrollable */}
          <div className="flex-1 overflow-y-auto" style={{ padding: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4.5px' }}>
              {rankings.map((teamRank) => {
                const isYourTeam = teamRank.team_id === yourTeamId;
                const isFirst = teamRank.rank === 1;
                const isSecond = teamRank.rank === 2;
                const isThird = teamRank.rank === 3;

                return (
                  <div
                    key={teamRank.team_id}
                    className="flex items-center justify-between rounded border"
                    style={{
                      padding: '6px',
                      backgroundColor: isYourTeam ? 'var(--team-primary-light)' : withAlpha(C.surface[855], 0.6),
                      borderColor: isYourTeam ? 'var(--team-primary)' : dynastyTheme.tokens.colors.neutral[700]
                    }}
                  >
                    {/* Rank + Team Name */}
                    <div className="flex items-center flex-1 min-w-0" style={{ gap: '6px' }}>
                      {/* Rank Badge */}
                      <div 
                        className="flex-shrink-0 rounded flex items-center justify-center font-bold"
                        style={{
                          width: '18px',
                          height: '18px',
                          fontSize: '9px',
                          background: isFirst
                            ? `linear-gradient(to bottom right, ${C.gold.soft}, ${C.amber[600]})`
                            : isSecond
                            ? `linear-gradient(to bottom right, ${C.grayCool}, ${C.mutedLight})`
                            : isThird
                            ? `linear-gradient(to bottom right, ${C.amber[600]}, ${C.amber[700]})`
                            : dynastyTheme.tokens.colors.neutral[700],
                          color: isFirst || isSecond ? C.black : C.white
                        }}
                      >
                        {teamRank.rank}
                      </div>

                      {/* Team Name */}
                      <div className="flex-1 min-w-0">
                        <div 
                          className="font-medium truncate"
                          style={{
                            fontSize: '11px',
                            color: isYourTeam ? dynastyTheme.tokens.colors.primary : C.white
                          }}
                        >
                          {teamRank.team_name}
                          {isYourTeam && (
                            <span className={dynastyTheme.classes.text.neutralLight} style={{ 
                              marginLeft: '3px', 
                              fontSize: '8px', 
                              fontWeight: 'normal' 
                            }}>
                              (You)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Value */}
                    <div 
                      className="flex-shrink-0 text-right font-bold"
                      style={{
                        fontSize: '11px',
                        marginLeft: '6px',
                        color: isYourTeam ? dynastyTheme.tokens.colors.primary : C.white
                      }}
                    >
                      {formatValue(selectedCategory, teamRank.value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t flex justify-end" style={{ 
            padding: '8px', 
            borderColor: dynastyTheme.tokens.colors.neutral[700] 
          }}>
            <button
              onClick={onClose}
              className={`${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.darkLighter} transition-colors rounded-lg`}
              style={{ 
                padding: '4.5px 9px', 
                fontSize: '11px' 
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileRankingsModal;

